import { useState, useEffect } from "react";
import { useInterval } from "./useInterval";

export type LoadingStep = {
  title: string;
  description?: React.ReactNode;
  image?: string;
};

export interface UseLoadingProgressParams {
  loading?: boolean;
  loadingSteps: Array<LoadingStep>;
  resetLoader?: boolean;
  averageTotalLoadingMillSec?: number;
}

export interface UseLoadingProgressReturn {
  progress: number;
  currentStep: LoadingStep;
}

export const useLoadingProgress = ({
  resetLoader,
  loading = true,
  loadingSteps,
  // Average total loading time in milliseconds taking from hsbc prod
  averageTotalLoadingMillSec = 12000,
}: UseLoadingProgressParams): UseLoadingProgressReturn => {
  const [step, setStep] = useState<LoadingStep>(loadingSteps[0]);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);

  // update 10% progress every 1/10 of the average total loading time
  const timer = useInterval(() => {
    setProgress(progress + 10);
  }, averageTotalLoadingMillSec / 10);

  useEffect(() => {
    if (resetLoader) {
      setProgress(0);
      setStep(loadingSteps[0]);
      setStepIndex(0);
    }
  }, [loadingSteps, resetLoader]);

  useEffect(() => {
    // if not last step, check if progress arrived the next step
    if (stepIndex < loadingSteps.length - 1) {
      if (
        Math.floor(progress) >=
        Math.floor((100 / loadingSteps.length) * (stepIndex + 1))
      ) {
        setStep(loadingSteps[stepIndex + 1]);
        setStepIndex(stepIndex + 1);
      }
    }
  }, [loadingSteps, progress, stepIndex]);

  useEffect(() => {
    if (!loading || progress > 110) {
      clearInterval(timer);
    }
  }, [loading, progress, timer]);

  return {
    progress,
    currentStep: step,
  };
};
